import React from 'react'

import { ChartSegmentSelectionScope, TotalSampleTag } from '../components/chart-segment/selection-scope'
import { SlotPlot } from '../slots/SlotPlot'
import type { SlotPlotShape } from '../slots/SlotPlot'
import { SlideFootnote } from './SlideFootnote'
import { SlideFrame } from './SlideFrame'
import { SlideHeaderChrome } from './SlideHeaderChrome'
import { presetSlot } from './presetSlots'

/**
 * The `rail` layout (plots plan Stage 2, AD): a square plot panel left, a text rail
 * right, under the header band. Geometry comes from the preset's bound slots
 * (`packages/slide-schemas/layouts/rail.json` plus the preset's overrides), so a slide
 * type on this layout is a binding: its plot, its panel look and its rail text.
 */
export interface RailLayoutProps {
  /** The slide type, which names its bound slots. */
  preset: string
  eyebrow: string
  clientLogoUrl: string | null
  testIds: { layout: string; plot: string; rail: string }
  /** The panel's look (radius, fill, overflow); its size and place are the slot's. */
  plotStyle: React.ComponentProps<typeof SlotPlot>['style']
  plot: React.ReactNode
  /** How the rail stacks its text (the choice rails spread it, matrix-categorical flows). */
  railStyle?: React.CSSProperties
  /** The slide's footer (its base, a split's small groups), bottom right. */
  footerText?: string | null
  children: React.ReactNode
}

export function RailLayout(props: RailLayoutProps) {
  const { preset, eyebrow, clientLogoUrl, testIds, plotStyle, plot, railStyle, footerText, children } = props
  const plotSlot = presetSlot(preset, 'plot')
  const plotRect = plotSlot.rect!
  const rail = presetSlot(preset, 'rail').rect!
  return (
    <ChartSegmentSelectionScope>
      <SlideFrame fullBleed>
        <div data-testid={testIds.layout} style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: 'var(--ds-slide-bg)' }}>
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
            <SlideHeaderChrome eyebrowText={eyebrow} clientLogoUrl={clientLogoUrl} eyebrowTextTransform="none" />
          </div>
          <SlotPlot shape={plotSlot.shape as SlotPlotShape} x={plotRect.x} y={plotRect.y} testId={testIds.plot} style={plotStyle}>
            {plot}
          </SlotPlot>
          <section
            data-testid={testIds.rail}
            style={{ position: 'absolute', left: rail.x, top: rail.y, width: rail.w, height: rail.h, ...railStyle, overflow: 'hidden' }}
          >
            {children}
            <TotalSampleTag style={{ position: 'absolute', bottom: 0, right: 0 }} />
          </section>
          <SlideFootnote text={footerText} />
        </div>
      </SlideFrame>
    </ChartSegmentSelectionScope>
  )
}
